import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Minus } from 'lucide-react';

/**
 * Reusable Modal with backdrop blur, ESC to close, focus trap.
 * Mounted via React Portals to prevent z-index/transform clipping.
 *
 * @param {'sm'|'md'|'lg'|'xl'|'2xl'|'full'} size
 */
const SIZE_MAP = {
  sm:   'max-w-md',
  md:   'max-w-lg',
  lg:   'max-w-2xl',
  xl:   'max-w-4xl',
  '2xl': 'max-w-6xl',
  '3xl': 'max-w-5xl',
  large: 'max-w-[75vw]',
  full: 'max-w-[95vw]',
};

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'lg',
  outsideClose = false,
  footer,
  className = '',
  headerClassName = '',
  closeButtonClassName = '',
  headerStyle = {},
  closeIcon = 'close',
  hideHeader = false,
  bodyClassName = '',
}) {
  const overlayRef = useRef(null);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const defaultHeaderStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)), url(${require('../../assets/Images/12.jpg')})`,
    ...headerStyle
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div
        ref={overlayRef}
        className="absolute inset-0 bg-black/60 shadow-2xl backdrop-blur-sm animate-in fade-in duration-300"
        onClick={() => outsideClose && onClose()}
      />

      {/* Panel */}
      <div
        className={`relative bg-white rounded-t-lg sm:rounded-xl shadow-modal w-full ${SIZE_MAP[size] || SIZE_MAP.lg} max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-[0.98] duration-300 ease-out ${className}`}
      >
        {/* Header with global luxury background image banner */}
        {!hideHeader && (
          <div 
            className={`flex items-center justify-between px-5 sm:px-6 py-4 border-b border-white/10 flex-shrink-0 relative overflow-hidden bg-cover bg-center ${headerClassName}`}
            style={defaultHeaderStyle}
          >
            <div className="flex-1 z-10 relative">
              {typeof title === 'string' ? (
                <span className="text-[13px] font-bold text-white font-serif tracking-widest uppercase">
                  {title}
                </span>
              ) : (
                title
              )}
            </div>
            <button
              onClick={onClose}
              className={`text-white/60 hover:text-white p-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer z-10 ${closeButtonClassName}`}
              title={closeIcon === 'minimize' ? "Minimize View" : "Close"}
            >
              {closeIcon === 'minimize' ? <Minus size={18} /> : <X size={18} />}
            </button>
          </div>
        )}

        {/* Body */}
        <div className={bodyClassName ? `flex-1 ${bodyClassName}` : 'overflow-y-auto flex-1 p-5 sm:p-6'}>{children}</div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-2 px-5 sm:px-6 py-3.5 border-t border-slate-100 bg-slate-50/50 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

Modal.displayName = 'Modal';
export { Modal };




